import { FC, useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import RatingComponent from 'components/ratings/RatingComponent'

import type { Space, SpaceType } from 'features/space/types/spaceDetail'

import { SpacePhotoCarousel } from './parts/SpacePhotoCarousel'
import { GgMapsServiceIntro } from 'features/space/components/GgMapsServiceIntro'
import { AddressTopSection } from './parts/AddressTopSection'
import { BasicInformationSection } from './parts/BasicInformationSection'
import type { SpaceInfoLabel } from './parts/BasicInformationSection'
import { StorageInformationSection } from './parts/StorageInformationSection'
import { SpaceInformationSection } from './parts/SpaceInformationSection'
import { IntroductionBlock } from './parts/IntroductionBlock'
import { SocialSection } from './parts/SocialSection'

import space_styles from './styles.module.scss'

type Props = {
  space: Space
  /** Google Maps 流入 (`?from_gg_maps=true`) 時のビューポート最適化レイアウト (Hotfix-532)。 */
  fromGgMap?: boolean
}

/**
 * /spaces/[uuid] の詳細表示。全 space_type で旧 /space (normal) ページの構造に統一し、
 * 最大預け入れ数の表示のみ space_type で出し分ける。
 */
export const SpaceDetailSection: FC<Props> = ({ space, fromGgMap = false }) => {
  const { t } = useTranslation()

  const isLocker = ['pudo', 'multiecube', 'locker', 'coin_locker'].includes(space.space_type)

  const nearestStation = space.nearby_stations[0]

  const basicInformationLabels: SpaceInfoLabel[] = [
    {
      className: 'distance',
      isPermit: !!nearestStation,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.WALKING_DISTANCE', {
        detailed_place: nearestStation?.station_name,
        distance_from_station: nearestStation?.distance,
      }),
      icon: '/basic_information_label_distance_icon.svg',
      meta: t('META.INFORMATION_DISTANCE_ICON'),
    },
    {
      className: 'canBookOnTheDay',
      isPermit: space.can_reserve_same_day,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.CAN_BOOK_ON_THE_DAY'),
      icon: '/basic_information_label_can_book_on_the_day_icon.svg',
      meta: t('META.CAN_BOOK_ON_THE_DAY_ICON'),
    },
    {
      className: 'onSiteSale',
      isPermit: space.features.accept_on_site_payment,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.ON_SITE_SALE'),
      icon: '/basic_information_on_site_sale_icon.svg',
      meta: t('META.ON_SITE_SALE_ICON'),
    },
    {
      className: 'hasElevator',
      isPermit: space.features.has_elevator,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.HAS_ELEVATOR'),
      icon: '/basic_information_label_has_elevator_icon.svg',
      meta: t('META.HAS_ELEVATOR_ICON'),
    },
    {
      className: 'hasWifi',
      isPermit: space.features.has_wifi,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.HAS_WIFI'),
      icon: '/basic_information_label_has_wifi_icon.svg',
      meta: t('META.HAS_WIFI_ICON'),
    },
    {
      className: 'hasCharge',
      isPermit: space.features.has_charge_spot,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.HAS_CHARGE'),
      icon: '/basic_information_label_has_charge_icon.svg',
      meta: t('META.HAS_CHARGE_ICON'),
    },
    {
      className: 'noSmoking',
      isPermit: space.features.no_smoking,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.NO_SMOKING'),
      icon: '/basic_information_no_smoking_icon.svg',
      meta: t('META.NO_SMOKING_ICON'),
    },
    {
      className: 'availableInEnglish',
      isPermit: space.features.english_supported,
      text: t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.AVAILABLE_IN_ENGLISH'),
      icon: '/basic_information_label_available_in_english_icon.svg',
      meta: t('META.AVAILABLE_IN_ENGLISH_ICON'),
    },
  ]

  const luggageLines = useMemo(() => {
    const spaceType: SpaceType = space.space_type

    switch (spaceType) {
      case 'locker':
      case 'coin_locker':
      case 'multiecube':
      case 'pudo': {
        const sizeOrder = { small: 0, medium: 1, large: 2 }

        return space.capacity
          .slice()
          .sort((a, b) => sizeOrder[a.size] - sizeOrder[b.size])
          .map(({ size, quantity }) => {
            if (size === 'small') {
              return t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.MAX_LOCKER_SMALL_SIZE', {
                smallSize: quantity,
              })
            }
            if (size === 'medium') {
              return t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.MAX_LOCKER_MEDIUM_SIZE', {
                mediumSize: quantity,
              })
            }
            return t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.MAX_LOCKER_LARGE_SIZE', {
              largeSize: quantity,
            })
          })
      }
      case 'normal':
      case 'jr_east':
      case 'japan_post':
      case 'jr_west':
      case 'nta':
      case 'apaman': {
        const findQuantity = (size: 'small' | 'large') =>
          space.capacity.find((capacity) => capacity.size === size)?.quantity || 0

        return [
          t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.MAX_BAG_SIZE', {
            bagSize: findQuantity('small'),
          }),
          t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.SUITECASE_SIZE', {
            suitecaseSize: findQuantity('large'),
          }),
        ]
      }
      default: {
        const exhaustiveCheck: never = spaceType
        return exhaustiveCheck
      }
    }
  }, [space.space_type, space.capacity, t])

  const reviewShareBox = (
    <div className={space_styles.reviewShareBox}>
      <div>
        {space.average_review_rating != null && (
          <div className={space_styles.review}>
            <div className={space_styles.reviewRatingAverage}>{space.average_review_rating}</div>
            <div className={space_styles.reviewRating}>
              <RatingComponent averageRating={space.average_review_rating} />
            </div>
            <div className={space_styles.reviewCount}>{space.total_review_count}</div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className={space_styles.spaceDetail}>
      <div className={space_styles.heading}>
        {fromGgMap && <GgMapsServiceIntro style={{ marginTop: 20 }} />}
        <h1 className={space_styles.spaceName}>{space.name}</h1>
        {isLocker && (
          <p className={space_styles.spaceDescription}>{t('LOCKER.LABEL.LOCKER_DESCRIPTION')}</p>
        )}
        {fromGgMap && <AddressTopSection space={space} />}
        {reviewShareBox}
        <SpacePhotoCarousel photos={space.space_photos} />
        <StorageInformationSection space={space} luggageLines={luggageLines} showReportButton>
          <SpaceInformationSection
            space={space}
            showHeading={false}
            showAddress={!fromGgMap}
            showIntroduction={false}
          />
        </StorageInformationSection>
        <BasicInformationSection labels={basicInformationLabels} showReportButton={false}>
          <IntroductionBlock space={space} />
        </BasicInformationSection>
        <SocialSection space={space} isLocker={isLocker} />
      </div>
    </div>
  )
}
